// Datumsrechnung fuer Meine Woche. Alles als lokale Kalendertage (YYYY-MM-DD), nie ueber UTC,
// sonst kippt der Tag nachts um. "heute" kommt vom Server (Europe/Berlin), siehe Woche.heute.

export const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const;
export const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONATE_KURZ = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];

export function ausIso(iso: string): Date {
  const [j, m, t] = iso.split('-').map(Number);
  return new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1, 12);
}

export function zuIso(d: Date): string {
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function plusTage(iso: string, tage: number): string {
  const d = ausIso(iso);
  d.setDate(d.getDate() + tage);
  return zuIso(d);
}

/** 0 = Montag ... 6 = Sonntag */
export function wochentagIndex(iso: string): number {
  return (ausIso(iso).getDay() + 6) % 7;
}

export function montagVon(iso: string): string {
  return plusTage(iso, -wochentagIndex(iso));
}

export function wochentag(iso: string): string {
  return WOCHENTAGE[wochentagIndex(iso)] ?? '';
}

export function tagDerZahl(iso: string): number {
  return ausIso(iso).getDate();
}

/** "Mittwoch, 30. September" */
export function langesDatum(iso: string): string {
  const d = ausIso(iso);
  return `${wochentag(iso)}, ${d.getDate()}. ${MONATE[d.getMonth()]}`;
}

/** "30. September" */
export function tagMonat(iso: string): string {
  const d = ausIso(iso);
  return `${d.getDate()}. ${MONATE[d.getMonth()]}`;
}

/** "Montag, 28.9." */
export function kurzesDatum(iso: string): string {
  const d = ausIso(iso);
  return `${wochentag(iso)}, ${d.getDate()}.${d.getMonth() + 1}.`;
}

/** "28. Sept. bis 4. Okt." */
export function wochenSpanne(montag: string): string {
  const a = ausIso(montag);
  const b = ausIso(plusTage(montag, 6));
  const links = a.getMonth() === b.getMonth() ? `${a.getDate()}.` : `${a.getDate()}. ${MONATE_KURZ[a.getMonth()]}`;
  return `${links} bis ${b.getDate()}. ${MONATE_KURZ[b.getMonth()]}`;
}

/** ISO-8601-Kalenderwoche */
export function kalenderwoche(iso: string): number {
  const d = ausIso(iso);
  const donnerstag = new Date(d);
  donnerstag.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan4 = new Date(donnerstag.getFullYear(), 0, 4, 12);
  return 1 + Math.round(((donnerstag.getTime() - jan4.getTime()) / 86_400_000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
}

/** Wie man einen Tag relativ zu heute nennt: "Heute", "Morgen", sonst Wochentag (bis 6 Tage) oder Datum. */
export function tagName(iso: string, heute: string): string {
  if (iso === heute) return 'Heute';
  if (iso === plusTage(heute, 1)) return 'Morgen';
  const diff = Math.round((ausIso(iso).getTime() - ausIso(heute).getTime()) / 86_400_000);
  // Wochentag nur innerhalb derselben Kalenderwoche; "Montag" fuer den naechsten Montag ist mehrdeutig (QA W9 Befund 13)
  if (diff > 1 && diff < 7 && montagVon(iso) === montagVon(heute)) return wochentag(iso);
  return kurzesDatum(iso);
}

/** ISO-Zeitpunkt -> lokaler Kalendertag */
export function tagVonZeitpunkt(zeitpunkt: string): string {
  return zuIso(new Date(zeitpunkt));
}

/** tagName mitten im Satz: "für morgen", "für Dienstag", "für Dienstag, 6.10." (QA Runde 2 N11). */
export function tagImSatz(iso: string, heute: string): string {
  const t = tagName(iso, heute);
  return t === 'Heute' || t === 'Morgen' ? t.toLowerCase() : t;
}

/** Satz mit genau einem Punkt am Ende ("6.10." bekommt keinen zweiten). */
export function satz(t: string): string {
  return /[.!?]$/.test(t) ? t : `${t}.`;
}
